// Canned replies for when someone asks about how Gnosis sources video: a
// creator who wants their clip out, a curious user, an investor doing
// diligence, or a law firm. Reference material only — nothing here writes.
// Admin only; the button that opens it is gated in Settings.
//
// The split these answers turn on: linking is defensible, copying is not, and
// the app is link-only (see functions/SCRAPING_POLICY.md). Platform terms are
// a separate question with a different answer, and the replies never claim
// compliance with them. Keep it that way — a denial in writing is worse than
// the scraping it denies.
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Copy, Check } from 'lucide-react';

interface ResponseGuideModalProps {
  onClose: () => void;
}

interface Reply {
  id: string;
  who: string;
  when: string;
  text: string;
}

const REPLIES: Reply[] = [
  {
    id: 'creator',
    who: 'A creator',
    when: 'Wants their clip out, or thinks you took it. Lead with the opt-out; it ends almost all of these.',
    text: `Your clip isn't stored on my servers. The app links to your post and it plays from Instagram, so the view is yours, and your handle is credited with a link back to your profile.

If you'd rather not be in it, tell me and I'll remove it today, no questions.`,
  },
  {
    id: 'casual',
    who: 'A user or a friend',
    when: '"Are you scraping?" or "is this legal?" asked casually. Answers it fully without inviting a follow-up.',
    text: `The videos play from Instagram and YouTube, we don't host them. Creators keep the views and get credited with a link back. Anyone who wants their clip out just asks and it's gone that day.`,
  },
  {
    id: 'pointed',
    who: 'An investor or a sharp user',
    when: 'Asked pointedly, in diligence. They already know the answer, so naming the risk and the mitigation beats a denial.',
    text: `Two separate things.

On copyright, we don't host or copy anything. The video plays from the creator's own platform, so there's nothing being redistributed.

On platform terms, how we find the playable link isn't something Instagram authorizes. The realistic downside is that they block us rather than sue us. We built an embed fallback for that case and can switch over in an afternoon.`,
  },
  {
    id: 'legal',
    who: 'Meta or a law firm',
    when: "Acknowledge receipt and nothing else. Don't explain the architecture in writing. Then flip to embeds and get advice.",
    text: `Received, thanks. I'm reviewing and will respond shortly.`,
  },
];

const NEVER_SAY = [
  ['"We don\'t scrape."', 'False, and a denial in writing is worse than the thing denied.'],
  ['"It\'s just embeds."', 'False while extraction is on.'],
  ['"It\'s all public, so it\'s fair game."', "Not a legal argument, and it reads as one."],
  ['"We\'re compliant with their terms."', 'We are not. Never claim this.'],
  ['"It\'s fully legal."', 'Describe what you do. Never argue that you\'re allowed to.'],
];

export default function ResponseGuideModal({ onClose }: ResponseGuideModalProps) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = async (r: Reply) => {
    try {
      await navigator.clipboard.writeText(r.text);
      setCopied(r.id);
      setTimeout(() => setCopied((c) => (c === r.id ? null : c)), 1600);
    } catch { /* clipboard may be blocked */ }
  };

  return createPortal(
    <div className="frame-modal-backdrop" onClick={onClose}>
      <div className="gift-modal rg-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="code-table-head" style={{ marginBottom: 10 }}>
          <span>How to respond</span>
        </div>
        <p className="rg-help">
          For questions about where the video comes from. The line that matters: linking is defensible, copying is not,
          and this app links. Platform terms are a separate question with a different answer, so none of these claim we
          comply with them.
        </p>

        {REPLIES.map((r) => (
          <div key={r.id} className="rg-block">
            <div className="rg-block-head">
              <span className="rg-who">{r.who}</span>
              <button
                type="button"
                className="rg-copy"
                onClick={() => copy(r)}
                aria-label={`Copy the reply for ${r.who}`}
              >
                {copied === r.id ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
              </button>
            </div>
            <p className="rg-when">{r.when}</p>
            <p className="rg-text">{r.text}</p>
          </div>
        ))}

        <div className="rg-never">
          <span className="rg-never-title">Never say</span>
          {NEVER_SAY.map(([line, why]) => (
            <p key={line} className="rg-never-row">
              <span className="rg-never-line">{line}</span>
              <span className="rg-never-why">{why}</span>
            </p>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
